import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mic, MessageSquare, MessagesSquare, Sparkles, ArrowRight, Timer, Brain, Target } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { goToIeltsAuth } from '@/lib/ieltsApi';

const PART_CARDS = [
  { key: 'p1', part: 'part_1', icon: MessageSquare, title: 'Part 1 · Interview',
    meta: '~4–5 minutes', desc: 'Short, friendly questions about everyday topics: hometown, work, hobbies, family, daily life.' },
  { key: 'p2', part: 'part_2', icon: MessagesSquare, title: 'Part 2 · Cue Card',
    meta: '1 min prep · 2 min talk', desc: 'A topic card with three bullet points. Speak for up to 2 minutes without interruption.' },
  { key: 'p3', part: 'part_3', icon: Mic, title: 'Part 3 · Discussion',
    meta: '~4–5 minutes', desc: 'Deeper discussion of abstract ideas linked to the Part 2 theme. Show range of opinion.' },
];

const FEATURES = [
  { icon: Timer, title: 'Real exam clocks', desc: '60-sec prep timer for Part 2, full duration timers for Part 1 and Part 3.' },
  { icon: Brain, title: 'AI band feedback', desc: 'Claude rates fluency, lexical, grammar and pronunciation with strengths + improvements.' },
  { icon: Target, title: 'Record OR type', desc: 'Speak into your mic for self-playback. Type your transcript to receive a band score.' },
];

const IeltsSpeakingLandingPage = () => {
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const start = (part) => goToIeltsAuth(`/ielts/speaking/practice?speaking_part=${part}`);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="ielts-speaking-landing">
      <Seo title="IELTS Speaking · Winhouse Tutors" canonical="/ielts/speaking"
        description="Practise all three IELTS Speaking parts with real-exam timers, cue cards, browser recording, and AI band feedback from Claude." />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <FloatingActionButtons />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-black text-white">
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.35) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 20%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%), linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
          }} />
          <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-20 sm:pt-36 sm:pb-24">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> IELTS Speaking AI
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                Speak with
                <span className="block text-[#D3D86F]">examiner-grade feedback.</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/75 max-w-2xl mt-6 leading-relaxed">
                Real Part 1, Part 2 and Part 3 prompts with cue cards, prep + speaking timers, browser recording,
                and instant AI band scores against all four IELTS Speaking descriptors.
              </p>
              <div className="flex flex-wrap gap-3 mt-9">
                <Button onClick={() => start('part_1')}
                  className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 text-base rounded-full"
                  data-testid="ielts-speaking-cta-start">
                  Start with Part 1 <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button onClick={() => goToIeltsAuth('/ielts/speaking/mock')}
                  variant="outline"
                  className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-7 h-12 text-base rounded-full"
                  data-testid="ielts-speaking-cta-mock">
                  Full Speaking Mock (~12 min)
                </Button>
                <Button onClick={() => navigate('/ielts/speaking/dashboard')}
                  variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 px-5 h-12 text-base rounded-full"
                  data-testid="ielts-speaking-cta-dashboard">
                  My Speaking Dashboard →
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Three real-exam parts</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Pick a part — start in seconds.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PART_CARDS.map(({ key, part, icon: Icon, title, meta, desc }) => (
              <button key={key} onClick={() => start(part)}
                className="text-left bg-white border border-gray-200 rounded-2xl p-6 hover:border-red-600 hover:-translate-y-0.5 transition-all group"
                data-testid={`ielts-speaking-card-${key}`}>
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-red-700" />
                </div>
                <div className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">{meta}</div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">{desc}</p>
                <span className="inline-flex items-center text-sm font-semibold text-black group-hover:text-red-700">
                  Begin practice <ArrowRight className="h-4 w-4 ml-1" />
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="bg-[#FAF9F6] py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="max-w-2xl mb-10">
              <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Why Winhouse Speaking</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">A real CBT speaking experience.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-black transition-all">
                  <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#D3D86F]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              120 original prompts. <span className="text-[#D3D86F]">AI feedback after every response.</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8 text-base sm:text-lg">
              The fastest way from "I freeze on the cue card" to "I know exactly how to hit Band 7."
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={() => start('part_2')}
                className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 rounded-full">
                Try a Part 2 Cue Card <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Link to="/ielts">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white px-7 h-12 rounded-full">
                  Back to IELTS Hub
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
export default IeltsSpeakingLandingPage;
